//src\PO-Process\components\SDCodeEditor.jsx

import { forwardRef, useEffect, useMemo, useImperativeHandle, useState } from "react";
import { getSkuCodes, updatePOData, updateSkuMapping } from "../api/api";
import { Autocomplete, TextField } from "@mui/material";
import { Toaster } from "../../components/Toaster";
import { getUser } from "../../helpers";

const SDCodeEditor = forwardRef((props, ref) => {
    const [options, setOptions] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const { rowIndex, context } = props;
    const user = getUser();

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const data = await getSkuCodes();
                const stringOptions = data.map((item) => item?.toString?.() || "");
    
                // Get all SD_Code values already in table except current row
                const existingCodes = (context.poData?.["Table Body"] || [])
                    .filter((_, idx) => idx !== rowIndex)
                    .map(row => row["SD_Code"]?.toString())
                    .filter(Boolean);

                // Filter out used codes
                let filtered = stringOptions.filter(code => !existingCodes.includes(code));

                // Allow current row's value back (if removed)
                const currentValue = props.value?.toString();
                if (currentValue && !filtered.includes(currentValue)) {
                    filtered.push(currentValue);
                }

                setOptions(filtered);

            } catch (error) {
                console.error("Failed to load SKU codes", error);
                Toaster.error("Failed to load SKU codes", error);
            }
        };
        fetchOptions();
    }, []);

    useImperativeHandle(ref, () => ({
        getValue() {
          return inputValue;
        },
        isPopup() {
            return true;
        },
        isCancelAfterEnd() {
            return false;
        }
    }));

    const handleSelect = async (selected) => {
        if (!selected) {
            Toaster.warning("No SD Code selected.");
            return;
        }

        const selectedStr = selected.toString();
        setInputValue(selectedStr);

        const matchedSKU = context.skuMaster.find(
            (item) => item?.SKU_ID?.toString() === selectedStr
        );
        const itemName = matchedSKU ? matchedSKU.Product_Name : "";

        try {
            await updatePOData({
                type: "item",
                filename: context.filename,
                old_label: "SD_Code",
                label: "SD_Code",
                new_value: selectedStr,
                rowIndex,
                user
            });

            if (itemName) {
                await updatePOData({
                    type: "item",
                    filename: context.filename,
                    old_label: "Item Name",
                    label: "Item Name",
                    new_value: itemName,
                    rowIndex,
                    user
                });
            }

            context.setPoData((prevPoData) => {
                const updatedTable = [...(prevPoData["Table Body"] || [])];
                updatedTable[rowIndex] = {
                    ...updatedTable[rowIndex],
                    SD_Code: selectedStr,
                    ["Item Name"]: itemName,
                };
                return {
                    ...prevPoData,
                    "Table Body": updatedTable,
                };
            });

            const rowData = context.poData["Table Body"][rowIndex];
            const itemCode = rowData?.["Item Code"];

            if (context.poData?.Customer && itemCode && selectedStr) {
                await updateSkuMapping({
                    Platform: context.poData.Customer,
                    Item_Code: itemCode,
                    SD_Code: selectedStr,
                });
                Toaster.success("sku_code_mapping updated:", itemCode, context.poData.Customer, selectedStr);
            } else {
                Toaster.warning("Missing data for sku_code_mapping update:", itemCode, context.poData.Customer, selectedStr);
            }

            Toaster.success("SD_Code & Item Name updated:", selectedStr, itemName);
        } catch (err) {
            console.error("Error updating SD_Code or Item Name:", err);
            Toaster.error("Error updating SD_Code or Item Name:", err);
        }

        requestAnimationFrame(() => {
            if (props.stopEditing) props.stopEditing();
        });
    };

    console.log("Current value:", inputValue); 
    console.log("Available options:", options);
    console.log("props.value:", props.value, typeof props.value);
    console.log("Normalized:", props.value?.toString());
    console.log("Options includes value:", options.includes(props.value?.toString()));

    if (!options.length) return null;

    return (
      <Autocomplete
        options={options}
        getOptionLabel={(option) => option.toString()}
        value={options.includes(props.value?.toString()) ? props.value.toString() : null}
        inputValue={inputValue}
        isOptionEqualToValue={(option, val) => option.toString() === val?.toString()}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue?.toString?.() || "");
        }}
        onChange={(event, newValue) => {
          if (newValue) handleSelect(newValue);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            placeholder="Select SD Code"
            autoFocus
          />
        )}
        disableClearable
        openOnFocus
        size="small"
        fullWidth
      />
    );
});

export default SDCodeEditor;